import * as z from "zod";
import { McpTransportSchema } from "../mcp.js";

/** The toggles IT can switch on for a template. Everything else a copy offers is fixed. */
export const DROK_TOGGLE_GROUP_NAMES = [
  "web",
  "computer",
  "shell",
  "routines",
  "subagents",
  "media",
  "bydsg",
] as const;
export const DrokToolGroupNameSchema = z.enum(DROK_TOGGLE_GROUP_NAMES);
export type DrokToolGroupName = z.infer<typeof DrokToolGroupNameSchema>;

/** Always on for a copy: they only answer the user or touch the employee's own conversation, memory and scratchpad. */
export const DROK_CORE_TOOLS: readonly string[] = [
  "ask_user",
  "message_user",
  "remember",
  "save_memory",
  "save_shared_memory",
  "recall_memory",
  "forget_memory",
  "search_history",
  "read_history",
  "knowledge_search",
  "knowledge_read",
  "knowledge_sources",
  "task_catalog",
  "scratchpad_list",
  "scratchpad_add",
  "scratchpad_update",
  "scratchpad_complete",
  "scratchpad_remove",
  "render_plot",
  "skill_read",
];

export const DROK_TOOL_GROUPS: Readonly<Record<DrokToolGroupName, readonly string[]>> = {
  web: ["web_search", "web_fetch"],
  computer: [
    "computer_observe",
    "computer_act",
    "browser_navigate",
    "browser_snapshot",
    "browser_act",
    "open_path",
    "launch_app",
    "request_takeover",
  ],
  shell: ["shell", "list_files", "read_file", "write_file", "attach_file"],
  routines: ["schedule_create", "schedule_list", "schedule_update", "schedule_cancel"],
  subagents: ["run_subagent"],
  media: ["generate_image", "generate_video"],
  /** BYDSG Meeting Copilot data and Google effects. Off unless IT enables it on the template. */
  bydsg: [
    "bydsg_create_person",
    "bydsg_update_person",
    "bydsg_create_project",
    "bydsg_update_project",
    "bydsg_schema_list",
    "bydsg_schema_define_field",
    "bydsg_schema_update_field",
    "bydsg_set_custom_fields",
    "bydsg_people",
    "bydsg_projects",
    "bydsg_weekly_updates",
    "bydsg_meetings",
    "bydsg_preview_meeting",
    "bydsg_schedule_meeting",
    "bydsg_submit_meeting_source",
    "bydsg_save_minutes",
    "bydsg_send_minutes",
    "bydsg_save_weekly_update",
  ],
};

/** Allowed only when the policy lists at least one Composio toolkit. */
export const DROK_CONNECT_APP_TOOL = "connect_app";

/**
 * Never offered on a copy. Most are agent-creating, Space-changing, secret, payment, call, cloud-agent or
 * bot-to-bot tools. Names are listed so the completeness test can prove every runtime tool is classified.
 */
export const DROK_OFF_TOOLS: readonly string[] = [
  "user_computer_shell",
  "add_mcp_server",
  "request_secret",
  "generate_secret",
  "list_secrets",
  "secret_request",
  "forget_secret",
  "request_purchase",
  "purchase_status",
  "paid_request",
  "cloud_agent_launch",
  "cloud_agent_status",
  "cloud_agent_reply",
  "cloud_agent_cancel",
  "end_call",
  "call_user",
  "board_tickets",
  "ticket_get",
  "ticket_create",
  "ticket_update",
  "ticket_comment",
  "skill_create",
  "skill_update",
  "skill_delete",
  "propose_instructions_update",
  "create_space",
  "spawn_bot",
  "update_bot",
  "list_fleet",
  "create_group",
  "update_group",
  "organize_bot",
  "set_child_instructions",
  "set_child_bot_model",
  "create_child_routine",
  "archive_bot",
  "delete_bot",
  "message_bot",
  "handoff_to_bot",
  "connect_agent",
  "respond_agent_connection",
  "message_agent",
];

const MAX_TOOLKITS = 50;
const MAX_TEXT = 200;

export const DrokToolkitSchema = z.object({
  slug: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(MAX_TEXT),
  logo: z.string().trim().max(2000).optional(),
});
export type DrokToolkit = z.infer<typeof DrokToolkitSchema>;

/** Compares toolkits the way composioSlugKey in the Composio connector does. */
export function drokToolkitKey(slug: string): string {
  return slug.trim().toLowerCase();
}

/** The shape of a Composio toolkit slug: lowercase letters, digits, underscores and hyphens. */
export const DROK_TOOLKIT_SLUG = /^[a-z0-9][a-z0-9_-]{0,99}$/;

/** Whether text, trimmed and lowercased, is a well-formed toolkit slug. */
export function isDrokToolkitSlug(slug: string): boolean {
  return DROK_TOOLKIT_SLUG.test(drokToolkitKey(slug));
}

export const DrokTemplatePolicySchema = z.object({
  toolGroups: z
    .array(DrokToolGroupNameSchema)
    .max(DROK_TOGGLE_GROUP_NAMES.length)
    .transform((groups) => [...new Set(groups)]),
  toolkits: z
    .array(DrokToolkitSchema)
    .max(MAX_TOOLKITS)
    .transform((toolkits) => {
      const seen = new Map<string, DrokToolkit>();
      for (const toolkit of toolkits) {
        const key = drokToolkitKey(toolkit.slug);
        if (!seen.has(key)) seen.set(key, toolkit);
      }
      return [...seen.values()];
    }),
});
export type DrokTemplatePolicy = z.infer<typeof DrokTemplatePolicySchema>;

export const DROK_SNAPSHOT_VERSION = 1;

const SnapshotBotSchema = z.object({
  name: z.string().trim().min(1).max(MAX_TEXT),
  title: z.string().max(MAX_TEXT),
  description: z.string().max(4_000),
  instructions: z.string().max(100_000),
  color: z.string().max(64),
  avatarSet: z.string().max(64),
  modelProvider: z.string().max(MAX_TEXT).nullable(),
  modelId: z.string().max(MAX_TEXT).nullable(),
  thinkingLevel: z.string().max(64).nullable(),
  memoryScope: z.string().max(64).nullable(),
  notifyOnFinish: z.boolean(),
});

const ItemKeySchema = z.string().trim().min(1).max(100);

export const DrokSnapshotRoutineSchema = z.object({
  itemKey: ItemKeySchema,
  name: z.string().trim().min(1).max(MAX_TEXT),
  prompt: z.string().max(50_000),
  crons: z.array(z.string().trim().min(1).max(200)).max(20),
  timezone: z.string().max(100),
  notify: z.boolean(),
  watch: z.unknown().nullable(),
  modelProvider: z.string().max(MAX_TEXT).nullable(),
  modelId: z.string().max(MAX_TEXT).nullable(),
  active: z.boolean(),
});
export type DrokSnapshotRoutine = z.infer<typeof DrokSnapshotRoutineSchema>;

export const DrokSnapshotSkillSchema = z.object({
  itemKey: ItemKeySchema,
  name: z.string().trim().min(1).max(MAX_TEXT),
  description: z.string().max(4_000),
  content: z.string().max(200_000),
  files: z
    .array(z.object({ path: z.string().min(1).max(500), content: z.string().max(200_000) }))
    .max(50),
});
export type DrokSnapshotSkill = z.infer<typeof DrokSnapshotSkillSchema>;

export const DrokMcpAuthModeSchema = z.enum(["employee", "shared"]);
export type DrokMcpAuthMode = z.infer<typeof DrokMcpAuthModeSchema>;

/** Credential field names only. Published versions never contain their values. */
const McpCredentialKeysSchema = z.record(z.string().min(1).max(200), z.literal(""));

export const DrokSnapshotMcpServerSchema = z.object({
  itemKey: ItemKeySchema,
  sourceServerId: ItemKeySchema,
  sourceBotId: ItemKeySchema,
  sourceUserId: ItemKeySchema,
  sourceSpaceId: ItemKeySchema,
  sourceRevision: z.number().int().positive(),
  authMode: DrokMcpAuthModeSchema,
  name: z.string().trim().min(1).max(120),
  description: z.string().max(2000),
  transport: McpTransportSchema,
  endpoint: z.string().max(2048).nullable(),
  command: z.string().max(512).nullable(),
  args: z.array(z.string().max(2048)).max(64),
  env: McpCredentialKeysSchema,
  files: McpCredentialKeysSchema,
  headers: McpCredentialKeysSchema,
  allowAllTools: z.boolean(),
  allowedTools: z.array(z.string().min(1).max(200)).max(500),
});
export type DrokSnapshotMcpServer = z.infer<typeof DrokSnapshotMcpServerSchema>;

export const DrokTemplateSnapshotSchema = z.object({
  /** Optional additions keep existing versions readable. */
  snapshotVersion: z.literal(DROK_SNAPSHOT_VERSION),
  bot: SnapshotBotSchema,
  routines: z.array(DrokSnapshotRoutineSchema).max(50),
  skills: z.array(DrokSnapshotSkillSchema).max(100),
  mcpServers: z.array(DrokSnapshotMcpServerSchema).max(50).default([]),
  knowledgeCollectionIds: z.array(z.string().min(1).max(200)).max(100).default([]),
});
export type DrokTemplateSnapshot = z.infer<typeof DrokTemplateSnapshotSchema>;

/** The built-in tool names a copy running this policy may be offered. */
export function copyAllowedBuiltinTools(policy: DrokTemplatePolicy): Set<string> {
  const allowed = new Set<string>(DROK_CORE_TOOLS);
  for (const group of policy.toolGroups) {
    for (const name of DROK_TOOL_GROUPS[group] ?? []) allowed.add(name);
  }
  if (policy.toolkits.length > 0) allowed.add(DROK_CONNECT_APP_TOOL);
  return allowed;
}

/** Every offered name that the policy does not allow, including tools upstream adds later. */
export function copyDisabledBuiltinTools(
  policy: DrokTemplatePolicy,
  offeredNames: Iterable<string>,
): string[] {
  const allowed = copyAllowedBuiltinTools(policy);
  const disabled = new Set<string>();
  for (const name of offeredNames) if (!allowed.has(name)) disabled.add(name);
  return [...disabled];
}
